import {
  PdfQuoteRendererData,
  ReactQuoteRendererData,
  stripHiddenOpenEndedPhases,
  stripPhasesBeforeAmendmentEffectiveFrom
} from '@sequencehq/quote-content'
import {
  calculateQuoteTotals,
  calculateQuotePhaseSubTotals,
  calculateQuotePriceCommitments,
  calculatePriceTotalsForPhases,
  Phase as TotalAmountPhase,
  apiPhaseToCalculatePhaseTotalAmountPhase,
  formatAddress,
  calculateDueOnStartDate,
  calculateQuotePhaseMinimumTotals
} from '@sequencehq/utils'
import { add, Duration } from '@sequencehq/utils/dates'

import { quoteContactToContactOption } from 'modules/Cube/communication/external/quotes.api.v1/adapters/quote.adapters.out'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import { CubeStatus, Customer } from 'modules/Cube/domain/cube.domain.types'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { phasesToQuotePhases } from 'Cube/communication/external/common.api.v1/adapters/common.adapters.out'

type PreviewRendererQueries = Pick<
  CubeDomainInterface['queries'],
  'rawData' | 'orderedPhases' | 'amendment'
>

const getPdfDownloadFileName = (
  data: CubeDomainInterface['queries']['rawData']['data']
): string => {
  return data.common.alias || data.common.title || data.common.id || 'quote'
}

const estimateExpiresAt = ({
  status,
  publishedAt,
  expiresIn = { days: 30 }
}: {
  status: CubeStatus
  publishedAt: Date | undefined
  expiresIn: Duration | undefined
}): string => {
  /**
   * For published and unedited quotes, use the publishedAt value as the date to calculate the expiresAt date.
   * Otherwise use the current time.
   */

  const baseDate =
    status === CubeStatus.QuoteDraft || !publishedAt ? new Date() : publishedAt

  return apiDatesAdapters.toApi(add(baseDate, expiresIn))
}

const toRendererAmendment = (
  queries: PreviewRendererQueries
): ReactQuoteRendererData['amendment'] => {
  const amendment = queries.rawData.data.quote.amendment

  if (!amendment) {
    return
  }

  const { contractValues } = queries.amendment

  if (!contractValues) {
    return
  }

  return {
    effectiveFrom: apiDatesAdapters.toApi(amendment.effectiveFrom),
    amendedContractValue: String(contractValues.amended),
    adjustmentTotal: String(contractValues.adjustment)
  }
}

export const orderPhasesForCommitments = (
  phases: Array<TotalAmountPhase & { id: string }>,
  orderedPhases: PreviewRendererQueries['orderedPhases']
): TotalAmountPhase[] => {
  const byId = new Map(phases.map(phase => [phase.id, phase]))
  const ordered = orderedPhases
    .map(orderedPhase => byId.get(orderedPhase.id))
    .filter((phase): phase is TotalAmountPhase & { id: string } =>
      Boolean(phase)
    )

  return ordered.length === phases.length ? ordered : []
}

export const previewRenderers = {
  reactPreview: {
    in: (queries: PreviewRendererQueries): ReactQuoteRendererData => {
      const data = queries.rawData.data
      const customers = data.customers

      const selectedCustomer: Customer | undefined =
        customers[data.common.customerId]

      const quoteContacts = data.quote.contacts

      const contacts = quoteContacts.map(contact =>
        quoteContactToContactOption(contact)
      )

      const preliminaryBillingStartDate = data.common.startDate
        ? apiDatesAdapters.toApi(data.common.startDate)
        : undefined
      const amendment = toRendererAmendment(queries)

      const visibleQuote = stripHiddenOpenEndedPhases({
        presentation: data.presentation,
        phases: phasesToQuotePhases(data)
      })
      const { presentation, phases, startDate } =
        stripPhasesBeforeAmendmentEffectiveFrom({
          ...visibleQuote,
          dealType: data.quote.dealType,
          preliminaryBillingStartDate,
          amendment
        })
      const totalsStartDate = startDate
        ? apiDatesAdapters.fromApi(startDate)
        : undefined
      const phasesForTotalsCalculation =
        apiPhaseToCalculatePhaseTotalAmountPhase(phases)

      const { totalDiscountedRate, totalFullRate } = calculateQuoteTotals({
        startDate: totalsStartDate,
        phases: phasesForTotalsCalculation,
        currency: data.common.currency ?? 'USD'
      })
      const phaseSubTotals = calculateQuotePhaseSubTotals({
        startDate: totalsStartDate,
        phases: phasesForTotalsCalculation,
        currency: data.common.currency ?? 'USD'
      })
      const totalDueOnStartDate = calculateDueOnStartDate({
        startDate: totalsStartDate,
        phases: phasesForTotalsCalculation,
        currency: data.common.currency ?? 'USD'
      })
      const phaseMinimumTotals = calculateQuotePhaseMinimumTotals({
        startDate: totalsStartDate,
        phases: phasesForTotalsCalculation,
        currency: data.common.currency ?? 'USD'
      })
      const priceCommitments = calculateQuotePriceCommitments({
        startDate: totalsStartDate,
        phases: orderPhasesForCommitments(
          phasesForTotalsCalculation,
          queries.orderedPhases
        )
      })
      const quotePriceTotalsPerPhase = calculatePriceTotalsForPhases({
        startDate: totalsStartDate,
        phases: orderPhasesForCommitments(
          phasesForTotalsCalculation,
          queries.orderedPhases
        ),
        currency: data.common.currency ?? 'USD'
      })

      return {
        title: data.common.title,
        prospectName: selectedCustomer?.legalName,
        // Deliberately `undefined`, not the empty list `formatAddress`
        // returns: the quote header hides itself on a falsy address, and an
        // empty array is truthy.
        prospectAddress: selectedCustomer?.address
          ? formatAddress(selectedCustomer.address)
          : undefined,
        merchantName: data.merchantBranding.legalCompanyName,
        merchantAddress: formatAddress(data.merchantBranding.address),
        merchantEmailAddress: data.merchantBranding.email,
        merchantLogoUrl: data.merchantBranding.logoUrl,
        // The duration and end date describe the whole contract, amendment or not
        phases: visibleQuote.phases,
        preliminaryBillingStartDate,
        amendment,
        expiresAt: estimateExpiresAt({
          status: data.common.status,
          publishedAt: data.quote.publishedAt,
          expiresIn: data.quote.expiresIn
        }),
        presentation,
        dealType: data.quote.dealType,
        contacts,
        counterSigners: data.quote.counterSigners,
        attachmentAssets: data.quote.attachmentAssets,
        totalDiscountedRate,
        totalFullRate,
        phaseSubTotals,
        totalDueOnStartDate,
        defaultDueDateDays: data.quote.defaultDueDateDays,
        currency: data.common.currency ?? 'USD',
        phaseMinimumTotals,
        priceCommitments,
        quotePriceTotalsPerPhase
      }
    }
  },
  pdfPreview: {
    in: (queries: PreviewRendererQueries): PdfQuoteRendererData => {
      const data = queries.rawData.data
      const customers = data.customers

      const selectedCustomer: Customer | undefined =
        customers[data.common.customerId]

      const preliminaryBillingStartDate = data.common.startDate
        ? apiDatesAdapters.toApi(data.common.startDate)
        : undefined
      const amendment = toRendererAmendment(queries)

      const visibleQuote = stripHiddenOpenEndedPhases({
        presentation: data.presentation,
        phases: phasesToQuotePhases(data)
      })
      const { presentation, phases, startDate } =
        stripPhasesBeforeAmendmentEffectiveFrom({
          ...visibleQuote,
          dealType: data.quote.dealType,
          preliminaryBillingStartDate,
          amendment
        })
      const totalsStartDate = startDate
        ? apiDatesAdapters.fromApi(startDate)
        : undefined
      const phasesForTotalsCalculation =
        apiPhaseToCalculatePhaseTotalAmountPhase(phases)

      const { totalDiscountedRate, totalFullRate } = calculateQuoteTotals({
        startDate: totalsStartDate,
        phases: phasesForTotalsCalculation,
        currency: data.common.currency ?? 'USD'
      })
      const phaseSubTotals = calculateQuotePhaseSubTotals({
        startDate: totalsStartDate,
        phases: phasesForTotalsCalculation,
        currency: data.common.currency ?? 'USD'
      })
      const totalDueOnStartDate = calculateDueOnStartDate({
        startDate: totalsStartDate,
        phases: phasesForTotalsCalculation,
        currency: data.common.currency ?? 'USD'
      })
      const priceCommitments = calculateQuotePriceCommitments({
        startDate: totalsStartDate,
        phases: orderPhasesForCommitments(
          phasesForTotalsCalculation,
          queries.orderedPhases
        )
      })
      const quotePriceTotalsPerPhase = calculatePriceTotalsForPhases({
        startDate: totalsStartDate,
        phases: orderPhasesForCommitments(
          phasesForTotalsCalculation,
          queries.orderedPhases
        ),
        currency: data.common.currency ?? 'USD'
      })

      return {
        title: data.common.title,
        prospectName: selectedCustomer?.legalName,
        // Deliberately `undefined`, not the empty list `formatAddress`
        // returns: the quote header hides itself on a falsy address, and an
        // empty array is truthy.
        prospectAddress: selectedCustomer?.address
          ? formatAddress(selectedCustomer.address)
          : undefined,
        merchantName: data.merchantBranding.legalCompanyName,
        merchantAddress: formatAddress(data.merchantBranding.address),
        merchantEmailAddress: data.merchantBranding.email,
        merchantLogoUrl: data.merchantBranding.logoUrl,
        merchantBrandColour: data.merchantBranding.primaryColour,
        // The duration and end date describe the whole contract, amendment or not
        phases: visibleQuote.phases,
        preliminaryBillingStartDate,
        amendment,
        expiresAt: estimateExpiresAt({
          status: data.common.status,
          publishedAt: data.quote.publishedAt,
          expiresIn: data.quote.expiresIn
        }),
        presentation,
        publishedAt: data.quote.publishedAt?.toISOString(),
        quoteNumber: getPdfDownloadFileName(data),
        dealType: data.quote.dealType,
        signers: [],
        includeSignaturePage: false,
        totalDiscountedRate,
        totalFullRate,
        currency: data.common.currency ?? 'USD',
        phaseSubTotals,
        priceCommitments,
        quotePriceTotalsPerPhase,
        totalDueOnStartDate,
        defaultDueDateDays: data.quote.defaultDueDateDays
      }
    }
  }
}
